import { Allergy } from '../types/Allergy';

// Determine API base URL based on environment
const isLocalhost = window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1';

const API_BASE_URL = isLocalhost 
  ? 'http://localhost:3001/api' 
  : 'https://davita-poc-api.demo.getvim.com/api';

interface AllergyChangeResponse {
  id: string;
  allergy_name: string | null;
  reaction: string | null;
  severity: string | null;
  allergy_type: string | null;
  status: string;
  was_added: boolean;
  add_screenshot_id: string;
  list_screenshot_id: string;
  add_screenshot_url: string | null;
  list_screenshot_url: string | null;
  patient_mrn: string | null;
  patient_name: string | null;
  patient_dob: string | null;
  created_at: string;
  result?: any;
  fhir_resource?: any;
}

interface ApiResponse {
  data: AllergyChangeResponse[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Fetches allergies for a patient by MRN from the davita-api
 * Uses the vim-canvas endpoint which doesn't require Auth0 authentication
 */
export async function fetchAllergiesByMRN(mrn: string): Promise<Allergy[]> {
  try {
    // Build query params
    const params = new URLSearchParams({ limit: '100' });
    if (mrn) {
      params.append('mrn', mrn);
    }

    const response = await fetch(`${API_BASE_URL}/vim-canvas/allergies?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      console.error('Failed to fetch allergies:', response.status, response.statusText);
      return [];
    }

    const apiResponse: ApiResponse = await response.json();

    // Transform API response to UI Allergy format
    return apiResponse.data.map(transformAllergyData);
  } catch (error) {
    console.error('Error fetching allergies:', error);
    return [];
  }
}

/**
 * Transforms API allergy data to UI Allergy format
 */
function transformAllergyData(data: AllergyChangeResponse): Allergy {
  // Get onset date from FHIR resource if available
  const onsetDate = data.result?.allergyChangeData?.fhir_resource?.onsetDateTime || 
                    new Date(data.created_at).toISOString().split('T')[0];

  // Map status from API to UI
  const status = mapStatus(data.status);
  
  // Get source system from result if available
  const sourceSystem = data.result?.allergyChangeData?.source || 'External System';

  // Create evidence screenshots from add and list screenshots using presigned URLs
  const evidenceScreenshots = [
    {
      id: data.add_screenshot_id,
      url: data.add_screenshot_url || getPlaceholderUrl(),
      caption: 'Allergy addition screen',
      timestamp: data.created_at,
    },
    {
      id: data.list_screenshot_id,
      url: data.list_screenshot_url || getPlaceholderUrl(),
      caption: 'Allergy list view',
      timestamp: data.created_at,
    },
  ];

  return {
    id: data.id,
    allergyName: data.allergy_name || 'Unknown',
    reaction: data.reaction || 'Not specified',
    severity: normalizeSeverity(data.severity),
    onsetDate,
    status,
    sourceSystem,
    detectedAt: data.created_at,
    reviewStatus: 'pending', // Always pending for new detections
    notes: data.result?.allergyChangeData?.reason || '',
    evidenceScreenshots,
    fhirResource: data.fhir_resource || data.result?.allergyChangeData?.fhir_resource || null,
  };
}

/**
 * Maps API status to UI status
 */
function mapStatus(apiStatus: string): 'Active' | 'Inactive' | 'Resolved' {
  // For now, all detected allergies are considered Active
  // This can be enhanced based on FHIR clinicalStatus
  return 'Active';
}

/**
 * Normalizes severity to UI expected values
 */
function normalizeSeverity(severity: string | null): 'Mild' | 'Moderate' | 'Severe' {
  if (!severity) return 'Moderate';
  
  const lower = severity.toLowerCase();
  if (lower.includes('severe') || lower === 'high') return 'Severe';
  if (lower.includes('mild') || lower === 'low') return 'Mild';
  return 'Moderate';
}

/**
 * Returns a placeholder image URL if screenshot URL is not available
 */
function getPlaceholderUrl(): string {
  return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';
}

